import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { Check, ChevronDown, Clock, Globe, Loader2, Mail, MapPin, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { Reveal, FadeIn, MaskedLines } from "@/components/Motion";
import { FIRM, SERVICE_OPTIONS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const schema = z.object({
  full_name: z
    .string()
    .min(2, "Please enter your full name")
    .max(120, "Name is too long"),
  company: z.string().max(160, "Company name is too long").optional().or(z.literal("")),
  email: z.string().email("Please enter a valid business email"),
  phone: z
    .string()
    .max(40, "Phone number is too long")
    .optional()
    .or(z.literal(""))
    .refine(
      (v) => !v || /^\+?[0-9\s\-().]{7,40}$/.test(v),
      "Please enter a valid phone number"
    ),
  service: z.string().min(1, "Please select a service"),
  message: z
    .string()
    .min(10, "Please describe your business requirements (at least 10 characters)")
    .max(5000, "Message is too long"),
  consent: z.boolean().refine((v) => v === true, "Please accept the Privacy Policy"),
  website: z.string().max(0).optional().or(z.literal("")),
});

function ContactCard({ icon: Icon, label, children, href, testid }) {
  const inner = (
    <>
      <span
        aria-hidden="true"
        className="flex h-12 w-12 shrink-0 items-center justify-center border border-gold/40 text-gold"
      >
        <Icon className="h-5 w-5" />
      </span>
      <span>
        <span className="block text-[11px] font-semibold uppercase tracking-[0.22em] text-charcoal/50">
          {label}
        </span>
        <span className="mt-1.5 block text-[15px] font-semibold text-navy">
          {children}
        </span>
      </span>
    </>
  );
  return href ? (
    <a
      href={href}
      data-testid={testid}
      className="group flex items-start gap-5 border border-navy/10 bg-white p-6 transition-all duration-300 hover:border-gold/50 hover:shadow-[0_18px_40px_-24px_rgba(16,36,58,0.35)]"
    >
      {inner}
    </a>
  ) : (
    <div data-testid={testid} className="flex items-start gap-5 border border-navy/10 bg-white p-6">
      {inner}
    </div>
  );
}

function FieldError({ id, message }) {
  if (!message) return null;
  return (
    <p id={id} role="alert" className="mt-2 text-[13px] font-medium text-red-600">
      {message}
    </p>
  );
}

function ContactForm() {
  const [searchParams] = useSearchParams();
  const preselected = SERVICE_OPTIONS.includes(searchParams.get("service"))
    ? searchParams.get("service")
    : "";
  const [serverError, setServerError] = useState(null);
  const [result, setResult] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    mode: "onBlur",
    defaultValues: {
      full_name: "",
      company: "",
      email: "",
      phone: "",
      service: preselected,
      message: "",
      consent: false,
      website: "",
    },
  });

  const serviceValue = watch("service");

  const onSubmit = async (values) => {
    setServerError(null);
    try {
      const res = await axios.post(`${API}/inquiries`, values);
      setResult({ ...res.data, email: values.email });
      toast.success(
        res.data.confirmation_sent
          ? "Inquiry sent — a confirmation email is on its way"
          : "Inquiry received"
      );
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg = "Something went wrong. Please try again, or reach us directly by telephone or email.";
      if (status === 429) {
        msg = "Too many inquiries from this connection. Please try again later.";
      } else if (status === 422) {
        msg = "Please review your entries and try again.";
      } else if (typeof detail === "string") {
        msg = detail;
      }
      setServerError(msg);
      toast.error(msg);
    }
  };

  if (result) {
    return (
      <div
        data-testid="contact-success"
        role="status"
        className="flex h-full flex-col items-start justify-center border border-navy/10 bg-white p-10 sm:p-14"
      >
        <span
          aria-hidden="true"
          className="flex h-14 w-14 items-center justify-center rounded-full bg-gold/15 text-gold"
        >
          <Check className="h-7 w-7" />
        </span>
        <h3 className="mt-8 font-serif text-3xl font-semibold text-navy">
          Thank you.
        </h3>
        {result.confirmation_sent ? (
          <p className="mt-4 text-base leading-relaxed text-charcoal/70">
            Your consultation inquiry has been received, and a confirmation
            email is on its way to{" "}
            <span className="font-semibold text-navy">{result.email}</span>. Our
            advisory team will review the information you have provided and
            respond within business hours (Monday–Friday, 8:00 AM–5:00 PM WAT).
          </p>
        ) : (
          <p className="mt-4 text-base leading-relaxed text-charcoal/70">
            Your consultation inquiry has been received and our advisory team
            will review the information you have provided. However, the
            confirmation email could not be delivered at this time. For a
            faster response, please also call{" "}
            <a href={FIRM.phoneHref} className="font-semibold text-navy underline decoration-gold">
              {FIRM.phone}
            </a>{" "}
            or email{" "}
            <a
              href={`mailto:${FIRM.email}`}
              className="font-semibold text-navy underline decoration-gold"
            >
              {FIRM.email}
            </a>
            .
          </p>
        )}
        <button
          type="button"
          data-testid="contact-success-reset"
          onClick={() => {
            setResult(null);
            reset();
          }}
          className="btn-outline-navy mt-10"
        >
          Send Another Inquiry
        </button>
      </div>
    );
  }

  const err = (name) => errors[name]?.message;

  return (
    <form
      noValidate
      onSubmit={handleSubmit(onSubmit)}
      data-testid="contact-form"
      className="relative border border-navy/10 bg-white p-7 sm:p-10"
    >
      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="full_name" className="field-label">
            Full Name <span className="text-gold">*</span>
          </label>
          <input
            id="full_name"
            data-testid="contact-form-name"
            autoComplete="name"
            placeholder="Your full name"
            aria-invalid={!!err("full_name")}
            aria-describedby={err("full_name") ? "full_name-error" : undefined}
            className={`field-input ${err("full_name") ? "field-input-error" : ""}`}
            {...register("full_name")}
          />
          <FieldError id="full_name-error" message={err("full_name")} />
        </div>

        <div>
          <label htmlFor="company" className="field-label">
            Company / Organization
          </label>
          <input
            id="company"
            data-testid="contact-form-company"
            autoComplete="organization"
            placeholder="Your organization"
            className="field-input"
            {...register("company")}
          />
        </div>

        <div>
          <label htmlFor="email" className="field-label">
            Business Email <span className="text-gold">*</span>
          </label>
          <input
            id="email"
            type="email"
            data-testid="contact-form-email"
            autoComplete="email"
            placeholder="you@company.com"
            aria-invalid={!!err("email")}
            aria-describedby={err("email") ? "email-error" : undefined}
            className={`field-input ${err("email") ? "field-input-error" : ""}`}
            {...register("email")}
          />
          <FieldError id="email-error" message={err("email")} />
        </div>

        <div>
          <label htmlFor="phone" className="field-label">
            Phone Number
          </label>
          <input
            id="phone"
            type="tel"
            data-testid="contact-form-phone"
            autoComplete="tel"
            placeholder="+234 ..."
            aria-invalid={!!err("phone")}
            aria-describedby={err("phone") ? "phone-error" : undefined}
            className={`field-input ${err("phone") ? "field-input-error" : ""}`}
            {...register("phone")}
          />
          <FieldError id="phone-error" message={err("phone")} />
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="service" className="field-label">
            Service of Interest <span className="text-gold">*</span>
          </label>
          <div className="relative">
            <select
              id="service"
              data-testid="contact-form-service"
              aria-invalid={!!err("service")}
              aria-describedby={err("service") ? "service-error" : undefined}
              className={`field-input appearance-none pr-12 ${err("service") ? "field-input-error" : ""} ${!serviceValue ? "text-charcoal/40" : ""}`}
              {...register("service")}
            >
              <option value="">Select a service…</option>
              {SERVICE_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <ChevronDown
              aria-hidden="true"
              className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-navy/50"
            />
          </div>
          <FieldError id="service-error" message={err("service")} />
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="message" className="field-label">
            Message / Business Requirements <span className="text-gold">*</span>
          </label>
          <textarea
            id="message"
            rows={6}
            data-testid="contact-form-message"
            placeholder="Tell us about your organization and the challenges you would like to address…"
            aria-invalid={!!err("message")}
            aria-describedby={err("message") ? "message-error" : undefined}
            className={`field-input resize-y ${err("message") ? "field-input-error" : ""}`}
            {...register("message")}
          />
          <FieldError id="message-error" message={err("message")} />
        </div>

        <div className="sm:col-span-2">
          <div className="flex items-start gap-4">
            <input
              id="consent"
              type="checkbox"
              data-testid="contact-form-consent"
              aria-invalid={!!err("consent")}
              aria-describedby={err("consent") ? "consent-error" : undefined}
              className="mt-1 h-5 w-5 shrink-0 cursor-pointer accent-[#C4A46A]"
              {...register("consent")}
            />
            <label htmlFor="consent" className="cursor-pointer text-sm leading-relaxed text-charcoal/70">
              I consent to the processing of my personal data as described in the{" "}
              <Link
                to="/privacy"
                data-testid="contact-privacy-link"
                className="font-semibold text-navy underline decoration-gold underline-offset-2"
              >
                Privacy Policy
              </Link>
              . <span className="text-gold">*</span>
            </label>
          </div>
          <FieldError id="consent-error" message={err("consent")} />
        </div>
      </div>

      {/* Honeypot — hidden from humans, catches bots */}
      <div aria-hidden="true" className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden">
        <label htmlFor="website">Website</label>
        <input
          id="website"
          data-testid="contact-form-website"
          tabIndex={-1}
          autoComplete="off"
          {...register("website")}
        />
      </div>

      {serverError && (
        <p
          data-testid="contact-form-error"
          role="alert"
          className="mt-6 border border-red-200 bg-red-50 px-5 py-4 text-sm font-medium text-red-700"
        >
          {serverError}
        </p>
      )}

      <button
        type="submit"
        data-testid="contact-form-submit"
        disabled={isSubmitting}
        className="btn-navy mt-8 w-full disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Sending…
          </>
        ) : (
          <>
            Submit Inquiry
            <Send className="h-4 w-4" aria-hidden="true" />
          </>
        )}
      </button>
    </form>
  );
}

export default function Contact() {
  return (
    <>
      <Seo
        title="Contact Us | Steven Philips & Associates Limited — Festac Town, Lagos"
        description="Contact Steven Philips & Associates Limited (SPA) in Festac Town, Lagos — management consultancy, accounting, auditing, tax, and ERP solutions. Call +234 803 329 6221 or +234 802 796 1263."
      />
      <section className="relative overflow-hidden bg-navy-deep pb-20 pt-40 lg:pb-28 lg:pt-52">
        <div className="grain absolute inset-0" aria-hidden="true" />
        <div
          aria-hidden="true"
          className="absolute -left-24 top-0 h-full w-1/2 bg-[radial-gradient(ellipse_at_top_left,rgba(196,164,106,0.12),transparent_60%)]"
        />
        <div className="container-x relative max-w-3xl">
          <FadeIn y={16}>
            <p className="eyebrow flex items-center gap-4">
              <span aria-hidden="true" className="h-px w-10 bg-gold" />
              Contact Us
            </p>
          </FadeIn>
          <h1 className="mt-7 font-serif text-4xl font-medium leading-[1.08] text-white sm:text-5xl lg:text-6xl">
            <MaskedLines
              delay={0.3}
              lines={[
                <span key="c1">Let&rsquo;s Transform Your</span>,
                <span key="c2">
                  Business <em className="font-medium italic text-gold">Together</em>
                </span>,
              ]}
            />
          </h1>
          <FadeIn delay={0.9}>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">
              Ready to take your organization to the next level? Get in touch
              with our expert advisory team today to discuss how we can assist
              you.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="bg-mist py-20 lg:py-28" data-testid="contact-main">
        <div className="container-x grid gap-14 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
          <Reveal>
            <div>
              <h2 className="font-serif text-3xl font-semibold text-navy">
                Contact Information
              </h2>
              <span aria-hidden="true" className="mt-8 block h-px w-16 bg-gold" />
              <div className="mt-10 space-y-5">
                <div
                  data-testid="contact-card-address"
                  className="flex items-start gap-5 border border-navy/10 bg-white p-6"
                >
                  <span
                    aria-hidden="true"
                    className="flex h-12 w-12 shrink-0 items-center justify-center border border-gold/40 text-gold"
                  >
                    <MapPin className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block text-[11px] font-semibold uppercase tracking-[0.22em] text-charcoal/50">
                      Office
                    </span>
                    <a
                      href={FIRM.addressHref}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="contact-address-link"
                      className="group mt-1.5 block text-[15px] font-semibold leading-relaxed text-navy hover:text-gold"
                    >
                      {FIRM.address.map((line) => (
                        <span key={line} className="block">
                          {line}
                        </span>
                      ))}
                      <span className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.14em] text-gold transition-colors group-hover:text-navy">
                        View on Map
                        <span
                          aria-hidden="true"
                          className="inline-block transition-transform duration-300 group-hover:translate-x-1"
                        >
                          →
                        </span>
                      </span>
                    </a>
                  </span>
                </div>

                <div
                  data-testid="contact-card-phone"
                  className="flex items-start gap-5 border border-navy/10 bg-white p-6"
                >
                  <span
                    aria-hidden="true"
                    className="flex h-12 w-12 shrink-0 items-center justify-center border border-gold/40 text-gold"
                  >
                    <Phone className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block text-[11px] font-semibold uppercase tracking-[0.22em] text-charcoal/50">
                      Telephone
                    </span>
                    <a
                      href={FIRM.phoneHref}
                      data-testid="contact-phone-link"
                      className="mt-1.5 block text-[15px] font-semibold text-navy hover:text-gold"
                    >
                      {FIRM.phone}
                    </a>
                    <a
                      href={FIRM.phone2Href}
                      data-testid="contact-phone-link-2"
                      className="block text-[15px] font-semibold text-navy hover:text-gold"
                    >
                      {FIRM.phone2}
                    </a>
                  </span>
                </div>

                <ContactCard
                  icon={Mail}
                  label="Email"
                  href={`mailto:${FIRM.email}`}
                  testid="contact-card-email"
                >
                  {FIRM.email}
                </ContactCard>

                <ContactCard
                  icon={Globe}
                  label="Website"
                  href={FIRM.website}
                  testid="contact-card-website"
                >
                  {FIRM.websiteLabel}
                </ContactCard>

                <ContactCard icon={Clock} label="Business Hours" testid="contact-card-hours">
                  Monday–Friday
                  <br />
                  8:00 AM–5:00 PM (West Africa Time)
                </ContactCard>
              </div>
              <p className="mt-10 max-w-sm text-sm leading-relaxed text-charcoal/55">
                Serious inquiries receive a considered response within business
                hours. All information you share is treated as confidential.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <ContactForm />
          </Reveal>
        </div>
      </section>
    </>
  );
}
